<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import { apiErrorCode, apiErrorMessage } from '../../../../api/coLiving/errors';
import { CARD_CLASS, FIELD_CLASS } from '../constants';
import { etaPayload, isoToInputInZone } from '../ticketHelpers';

const TENANT_MAX = 2000;
const INTERNAL_MAX = 5000;

// The three notes of a ticket: the ETA and the note the tenant reads (both are
// a status step: from New, PIC notified or Reopened they move the ticket to In
// progress) and the internal note. A tenant note may not hold a phone number
// or a staff name: the server refuses it (422) and the refusal is shown here.
export default {
  name: 'NotesPanel',
  props: {
    ticket: { type: Object, required: true },
    canEdit: { type: Boolean, default: false },
    timezone: { type: String, default: '' },
  },
  emits: ['changed'],
  data() {
    return {
      cardClass: CARD_CLASS,
      fieldClass: FIELD_CLASS,
      tenantMax: TENANT_MAX,
      internalMax: INTERNAL_MAX,
      eta: this.toInput(this.ticket.eta),
      tenantNote: this.ticket.tenant_note || '',
      internalNote: this.ticket.internal_note || '',
      errors: {},
      busy: '',
    };
  },
  watch: {
    ticket(ticket) {
      this.eta = this.toInput(ticket.eta);
      this.tenantNote = ticket.tenant_note || '';
      this.internalNote = ticket.internal_note || '';
    },
  },
  methods: {
    ...mapActions('coLivingTickets', ['saveNotes']),
    // The API gives UTC; the input shows the account timezone, the same zone
    // the server reads a new offset-less ETA in.
    toInput(iso) {
      return isoToInputInZone(iso, this.timezone);
    },
    async save(field, body) {
      this.busy = field;
      this.errors = {};
      try {
        await this.saveNotes({ id: this.ticket.id, body });
        useAlert(this.$t('CO_LIVING.COMMON.SAVED'));
        this.$emit('changed');
      } catch (err) {
        const code = apiErrorCode(err);
        if (code === 'tenant_note_contains_contact') {
          this.errors = { tenant_note: 'CONTACT' };
        } else if (code === 'invalid_eta') {
          this.errors = { eta: 'INVALID_ETA' };
        } else {
          useAlert(
            apiErrorMessage(this, err, 'CO_LIVING.TICKETS.NOTES.FAILED')
          );
          if (code === 'step_not_allowed') this.$emit('changed');
        }
      } finally {
        this.busy = '';
      }
    },
    saveEta() {
      return this.save('eta', { eta: etaPayload(this.eta) });
    },
    saveTenantNote() {
      return this.save('tenant_note', {
        tenant_note: this.tenantNote.trim() || null,
      });
    },
    saveInternalNote() {
      return this.save('internal_note', {
        internal_note: this.internalNote.trim() || null,
      });
    },
    errorText(field) {
      return this.errors[field]
        ? this.$t(`CO_LIVING.TICKETS.NOTES.ERRORS.${this.errors[field]}`)
        : '';
    },
  },
};
</script>

<template>
  <section :class="cardClass" data-testid="co-living-notes">
    <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100">
      {{ $t('CO_LIVING.TICKETS.NOTES.TITLE') }}
    </h3>

    <div class="mt-4 grid grid-cols-1 gap-5">
      <div>
        <label
          for="co-living-eta"
          class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
        >
          {{ $t('CO_LIVING.TICKETS.NOTES.ETA') }}
        </label>
        <div class="flex items-center gap-2">
          <input
            id="co-living-eta"
            v-model="eta"
            type="datetime-local"
            :disabled="!canEdit"
            class="max-w-xs"
            :class="[fieldClass]"
            data-testid="co-living-eta-input"
          />
          <woot-button
            v-if="canEdit"
            size="small"
            :is-loading="busy === 'eta'"
            data-testid="co-living-eta-save"
            @click="saveEta"
          >
            {{ $t('CO_LIVING.COMMON.SAVE') }}
          </woot-button>
        </div>
        <p
          v-if="errors.eta"
          class="mt-1 text-xs text-red-500"
          data-testid="co-living-eta-error"
        >
          {{ errorText('eta') }}
        </p>
        <p v-else class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.TICKETS.NOTES.ETA_HINT') }}
        </p>
      </div>

      <div>
        <label
          for="co-living-tenant-note"
          class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
        >
          {{ $t('CO_LIVING.TICKETS.NOTES.TENANT') }}
        </label>
        <textarea
          id="co-living-tenant-note"
          v-model="tenantNote"
          rows="3"
          :maxlength="tenantMax"
          :disabled="!canEdit"
          :class="[fieldClass, errors.tenant_note ? 'border-red-500' : '']"
          data-testid="co-living-tenant-note-input"
        />
        <p
          v-if="errors.tenant_note"
          class="mt-1 text-xs text-red-500"
          data-testid="co-living-tenant-note-error"
        >
          {{ errorText('tenant_note') }}
        </p>
        <p v-else class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.TICKETS.NOTES.TENANT_HINT') }}
        </p>
        <woot-button
          v-if="canEdit"
          class="mt-2"
          size="small"
          :is-loading="busy === 'tenant_note'"
          data-testid="co-living-tenant-note-save"
          @click="saveTenantNote"
        >
          {{ $t('CO_LIVING.COMMON.SAVE') }}
        </woot-button>
      </div>

      <div>
        <label
          for="co-living-internal-note"
          class="block mb-1 text-sm text-slate-700 dark:text-slate-200"
        >
          {{ $t('CO_LIVING.TICKETS.NOTES.INTERNAL') }}
        </label>
        <textarea
          id="co-living-internal-note"
          v-model="internalNote"
          rows="3"
          :maxlength="internalMax"
          :disabled="!canEdit"
          :class="fieldClass"
          data-testid="co-living-internal-note-input"
        />
        <p class="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.TICKETS.NOTES.INTERNAL_HINT') }}
        </p>
        <woot-button
          v-if="canEdit"
          class="mt-2"
          size="small"
          :is-loading="busy === 'internal_note'"
          data-testid="co-living-internal-note-save"
          @click="saveInternalNote"
        >
          {{ $t('CO_LIVING.COMMON.SAVE') }}
        </woot-button>
      </div>
    </div>
  </section>
</template>
